import {
  InsResponseDTO,
  InsVerificationResponseDTO,
  PatientInsHistory,
} from '@/services/InsiService'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  FindPatientsQuery,
  PatientDto,
  SuggestPatientsQuery,
} from '@services/api'
import { BillingService } from '@services/BillingService'
import { DocumentDTO, InvoiceDto, PatientStats } from '@services/dtos'
import { InsiService } from '@services/InsiService'
import { PaginatedDto } from '@services/extendedType'
import {
  PatientMergeHistoryDto,
  PatientServiceV2,
} from '@services/PatientService'
import { updatePatient } from '@state/thunks/authThunk'
import {
  doCreatePatient,
  doFetchMergeHistory,
  fetchOnePatientById,
  fetchPatients,
  doGetCouverture,
  findInvoices,
  fetchSuggestPatients,
  doGetAllPatientStats,
} from '@state/thunks/patientsThunk'
import { CouvertureDto } from 'src/common/interfaces'

export type PatientsState = {
  patientsIds: number[]
  patients: PatientDto[]
  patientsFilter: FindPatientsQuery
  suggestedFilters: SuggestPatientsQuery
  suggestedPatients: PaginatedDto<PatientDto>
  suggestedLoading: boolean
  currentPatientId: number
  currentPatientInfo: PatientDto | null
  patientCouverture: CouvertureDto | null
  mergeHistories: PatientMergeHistoryDto[]
  patientCoverage: CouvertureDto | null
  patientInsHistory: PatientInsHistory | null
  selectedInsiSearchResult: InsResponseDTO | null
  // TODO: move this somewhere that makes sense
  patientCardReaderId: string | null
  documents: PaginatedDto<DocumentDTO>
  loadingInvoices: boolean
  insError: string | null
  insInfo: string | null
  stats?: PatientStats | null
  patientStatsLoading: boolean
}

const initialState: PatientsState = {
  suggestedPatients: {
    datas: [],
    totalCount: 0,
  },
  suggestedLoading: false,
  suggestedFilters: {
    limit: 20,
    page: 0,
  },
  patientsIds: [],
  patients: [],
  currentPatientId: 0,
  currentPatientInfo: null,
  patientCouverture: null,
  patientsFilter: {
    limit: 20,
    page: 1,
    siteId: 1,
  },
  mergeHistories: [],
  patientCoverage: null,
  patientInsHistory: null,
  patientCardReaderId: null,
  documents: { datas: [], totalCount: 0 },
  loadingInvoices: false,
  selectedInsiSearchResult: null,
  insError: null,
  insInfo: '',
  stats: null,
  patientStatsLoading: false,
}

export const doCreateCoverege = createAsyncThunk<
  CouvertureDto,
  {
    patientId: number
    dto: CouvertureDto
  }
>('patients/createCoverage', async ({ patientId, dto }) => {
  const response = await BillingService.createCouverture(patientId, dto)
  return response
})

export const doUpdateCouverture = createAsyncThunk<
  CouvertureDto,
  { id: number; dto: CouvertureDto }
>('patients/updateCoverage', async ({ id, dto }) => {
  const response = await BillingService.updateCouverture(id, dto)
  return response
})

export const doGetPatientDocuments = createAsyncThunk<
  PaginatedDto<DocumentDTO>,
  { patientId: number; page?: number; take?: number }
>(
  'patients/getDocuments',
  async ({ patientId, page, take }, { rejectWithValue }) => {
    try {
      return await PatientServiceV2.getDocuments(patientId, { page, take })
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const doGetPatientInsHistory = createAsyncThunk<
  PatientInsHistory | null,
  number
>('patients/getInsHistory', async (patientId) => {
  try {
    const response = await InsiService.getPatientInsHistory(patientId)
    return response
  } catch (err) {
    return null
  }
})

export const doSavePatientInsHistory = createAsyncThunk<
  PatientInsHistory,
  { patientId: number; insHistory: PatientInsHistory }
>('patients/saveInsHistory', async ({ patientId, insHistory }) => {
  const response = await InsiService.savePatientInsHistory(
    patientId,
    insHistory,
  )
  return response
})

const patientsSlice = createSlice({
  name: 'patients',
  initialState,
  reducers: {
    addPatient: (state, action: PayloadAction<PatientDto>) => {
      state.patientsIds.push(action.payload.id)
    },
    getPatient: (state, action: PayloadAction<number[]>) => {
      state.currentPatientId = action.payload[0]
      state.patientsIds = action.payload.map((patientId: number) => patientId)
    },
    getPatientById: (state, action: PayloadAction<number>) => {
      state.currentPatientId = action.payload
    },
    setCouverture: (state, action: PayloadAction<CouvertureDto | null>) => {
      state.patientCouverture = action.payload
    },
    setPatients: (state, action: PayloadAction<PatientDto[]>) => {
      state.patients = action.payload
    },
    setPatientsFilter: (state, action: PayloadAction<FindPatientsQuery>) => {
      state.patientsFilter = { ...state.patientsFilter, ...action.payload }
    },
    setSuggestedFilters: (
      state,
      action: PayloadAction<SuggestPatientsQuery>,
    ) => {
      state.suggestedFilters = { ...state.suggestedFilters, ...action.payload }
    },
    setReaderId: (state, action: PayloadAction<string>) => {
      state.patientCardReaderId = action.payload
    },
    setPatientDetails: (state, action: PayloadAction<PatientDto | null>) => {
      state.currentPatientInfo = action.payload
    },
    setPatientDocuments: (
      state,
      action: PayloadAction<PaginatedDto<DocumentDTO>>,
    ) => {
      state.documents = action.payload
    },
    setSelectedInsiSearchResult: (
      state,
      action: PayloadAction<InsResponseDTO | null>,
    ) => {
      state.selectedInsiSearchResult = action.payload
    },
    setPatientInsHistory: (
      state,
      action: PayloadAction<PatientInsHistory | null>,
    ) => {
      state.patientInsHistory = action.payload
    },
    setInsError: (state, action: PayloadAction<string | null>) => {
      state.insError = action.payload
    },
    setInsInfo: (state, action: PayloadAction<string | null>) => {
      state.insInfo = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSuggestPatients.pending, (state) => {
      state.suggestedLoading = true
    })
    builder.addCase(fetchSuggestPatients.rejected, (state) => {
      state.suggestedLoading = false
    })
    builder.addCase(
      fetchPatients.fulfilled,
      (state, { payload }: PayloadAction<PaginatedDto<PatientDto>>) => {
        state.patients = payload.datas
      },
    )
    builder.addCase(
      fetchSuggestPatients.fulfilled,
      (state, { payload }: PayloadAction<PaginatedDto<PatientDto>>) => {
        state.suggestedPatients = payload
        state.suggestedLoading = false
      },
    )
    builder.addCase(fetchOnePatientById.fulfilled, (state, action) => {
      state.currentPatientInfo = action.payload as PatientDto
    })
    builder.addCase(doGetCouverture.fulfilled, (state, action) => {
      state.patientCouverture = action.payload
    })
    builder.addCase(updatePatient.fulfilled, (state, action) => {
      state.currentPatientInfo = action.payload
    })
    builder.addCase(doFetchMergeHistory.fulfilled, (state, action) => {
      state.mergeHistories = action.payload
    })
    builder.addCase(doCreatePatient.fulfilled, (state, action) => {
      state.patients.push(action.payload)
    })
    builder.addCase(doGetPatientDocuments.fulfilled, (state, action) => {
      state.documents = action.payload
    })
    builder.addCase(doUpdateCouverture.fulfilled, (state, action) => {
      state.patientCouverture = action.payload
    })
    builder.addCase(findInvoices.pending, (state) => {
      state.loadingInvoices = true
    })
    builder.addCase(findInvoices.rejected, (state) => {
      state.loadingInvoices = false
    })
    builder.addCase(
      findInvoices.fulfilled,
      (
        state,
        { payload, meta }: PayloadAction<InvoiceDto[], string, { arg: number }>,
      ) => {
        const patientId = meta.arg
        const patientIndex = state.patients.findIndex(
          ({ id }) => +patientId === id,
        )
        if (patientIndex !== -1) {
          state.patients[patientIndex].invoices = payload
        }
        state.loadingInvoices = false
      },
    )
    builder.addCase(doGetPatientInsHistory.fulfilled, (state, action) => {
      state.patientInsHistory = action.payload
    })
    builder.addCase(doSavePatientInsHistory.fulfilled, (state, action) => {
      state.patientInsHistory = action.payload
    })
    builder
      .addCase(doGetAllPatientStats.pending, (state) => {
        state.patientStatsLoading = true
      })
      .addCase(doGetAllPatientStats.fulfilled, (state, action) => {
        state.stats = action.payload
        state.patientStatsLoading = false
      })
      .addCase(doGetAllPatientStats.rejected, (state) => {
        state.patientStatsLoading = false
      })
  },
})

export const {
  addPatient,
  getPatient,
  setSuggestedFilters,
  setCouverture,
  setPatientsFilter,
  setPatients,
  setReaderId,
  setPatientDetails,
  setPatientDocuments,
  setSelectedInsiSearchResult,
  setPatientInsHistory,
  setInsError,
  setInsInfo,
} = patientsSlice.actions

export default patientsSlice.reducer
